import { ChangeEvent, FC, KeyboardEvent, useEffect, useState } from "react";
import {
  Chip,
  StyledIcon,
  ErrorMessage,
  Container,
  TextFieldContainer,
} from "./MultiValueTextField.ui";
import { BaseComponentProps } from "../types";
import { TextField } from "../TextField";

export enum MultiValueTextFieldTestIds {
  Component = "multi-value-text-field",
}

export enum ValidationType {
  Email = "email",
}

const regexByValidationType: {
  [key in ValidationType]: RegExp;
} = {
  [ValidationType.Email]: /^[^\s@]+@[^\s.@]+(\.[^\s.@]+)*$/,
};

export type Chip = {
  label: string;
  value: string;
};

type MultiValueTextFieldProps = Omit<BaseComponentProps<"input">, "value"> & {
  id?: string;
  className?: string;
  testId?: string;
  placeholder?: string;
  errorMessage?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValidate?: (isValid: boolean) => void;
  value?: Chip[];
  onAddChip?: (chip: Chip) => void;
  onCloseChip?: (chip: Chip) => void;
  validation?: ValidationType;
  customValidation?: RegExp;
};

export const MultiValueTextField: FC<MultiValueTextFieldProps> = ({
  id,
  className,
  testId,
  placeholder,
  value: controlledValue,
  onAddChip,
  onCloseChip,
  onChange,
  onValidate,
  errorMessage = "Invalid input.",
  validation,
  customValidation,
  ...inputProps
}) => {
  const [currentText, setCurrentText] = useState("");
  const [value, setValue] = useState(controlledValue || []);
  const [isValid, setIsValid] = useState(true);

  const isControlledComponent = controlledValue !== undefined;

  useEffect(() => {
    setValue(controlledValue || []);
  }, [controlledValue]);

  useEffect(() => {
    handleValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const isValidInputText = (text: string): boolean => {
    let isValidInputText = true;

    if (validation) {
      const myRegex: RegExp = regexByValidationType[validation];
      isValidInputText = isValidInputText && myRegex.test(text);
    }
    if (isValidInputText && customValidation) {
      isValidInputText = isValidInputText && customValidation.test(text);
    }

    return isValidInputText;
  };

  const handleValidation = () => {
    const isValidValue = value.every((chip) => isValidInputText(chip.value));

    setIsValid(isValidValue);
    onValidate?.(isValidValue);
  };

  const handleOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (currentText && (event.key === "Enter" || event.code === "Space")) {
      const newChip: Chip = {
        label: currentText,
        value: currentText,
      };
      if (!isControlledComponent) {
        setValue([...value, newChip]);
      }

      setCurrentText("");
      onAddChip?.(newChip);
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCurrentText(event.target.value.trim());
    onChange?.(event);
  };

  const handleCloseChip = (chipToDelete: Chip) => {
    if (!isControlledComponent) {
      setValue(value.filter((chip) => chip.value !== chipToDelete.value));
    }

    onCloseChip?.(chipToDelete);
  };

  return (
    <Container
      id={id}
      className={className}
      data-testid={testId ?? MultiValueTextFieldTestIds.Component}
    >
      <TextFieldContainer className={isValid ? "" : "danger"}>
        {value.map((chip, i) => (
          <Chip key={`${chip.value}-${i}`} className={isValid ? "" : "danger"}>
            {chip.label}
            <StyledIcon
              iconName="x-close"
              iconSize="small"
              plain
              onClick={(): void => handleCloseChip(chip)}
            />
          </Chip>
        ))}
        <TextField
          value={currentText}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          placeholder={placeholder}
          {...inputProps}
        />
      </TextFieldContainer>
      {!isValid && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </Container>
  );
};
